import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Spin, message } from 'antd';
import {
  adoptEacRecommendation,
  getEacProjectDetail,
  getEacProjectList,
  runEacProcessStream,
  type EacProjectItem,
} from '../../api';
import { useConfig } from '../../hooks/useConfig';
import { usePageEventQueue } from '../scenarioTask/hooks/usePageEventQueue';
import ContentEmptyState from './components/ContentEmptyState';
import DetectionContent from './components/DetectionContent';
import LogPanel from './components/LogPanel';
import PageHeader from './components/PageHeader';
import AppHeader from '../../components/AppHeader';
import ProjectSidebar from './components/ProjectSidebar';
import type {
  EacFirstRoundAnswer,
  EacProcessPayload,
  EacProcessPayloadSecond,
  EacProcessStreamItem,
  EacSecondRoundAnswer,
  RecommendationDecision,
} from './types';
import {
  buildFirstRoundPayload,
  buildSecondRoundPayload,
  createStageLogItems,
  extractAdoptPayload,
  getFirstRoundProcessNames,
  getProcessStatusFromStreamItem,
  getSecondRoundProcessNames,
  getVisibleLogItems,
  isRiskFree,
  parseEacProcessStreamChunk,
  parseFirstRoundAnswer,
  parseSecondRoundAnswer,
  upsertLogStatus,
} from './utils';

const EbpEacEnhancementPage: React.FC = () => {
  const REJECT_ACTION_LOG_NAME = '用户放弃纠偏方案';
  const RETRY_ACTION_LOG_NAME = '用户触发重新推荐纠偏方案';
  const ADOPT_ACTION_LOG_NAME = '用户采纳纠偏方案';

  const [selectedProjectCode, setSelectedProjectCode] = useState<string>();
  const [messageApi, contextHolder] = message.useMessage();
  const { globalConfig } = useConfig();
  const { createQueue, enqueue, flush, destroy } =
    usePageEventQueue<EacProcessStreamItem>();
  const abortControllerRef = useRef<AbortController | null>(null);
  const firstRoundAnswerRef = useRef<EacFirstRoundAnswer | null>(null);
  const secondRoundAnswerRef = useRef<EacSecondRoundAnswer | null>(null);
  const lastQueueConsumeAtRef = useRef(0);
  const activeProcessRunIdRef = useRef(0);
  const secondRoundTaskIdRef = useRef(0);

  const [detectStarted, setDetectStarted] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [secondRoundPending, setSecondRoundPending] = useState(false);
  const [latestDetectTime, setLatestDetectTime] = useState<Date | null>(null);
  const [logItems, setLogItems] = useState(createStageLogItems([], 1));
  const [firstRoundAnswer, setFirstRoundAnswer] =
    useState<EacFirstRoundAnswer | null>(null);
  const [secondRoundAnswer, setSecondRoundAnswer] =
    useState<EacSecondRoundAnswer | null>(null);
  const [recommendationDecision, setRecommendationDecision] =
    useState<RecommendationDecision>(null);
  const [adoptSubmitting, setAdoptSubmitting] = useState(false);
  const [detectionProject, setDetectionProject] =
    useState<EacProjectItem | null>(null);

  const {
    data: projectList = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ebp-eac-enhancement-project-list'],
    queryFn: () => getEacProjectList(),
  });

  const pageConsumeIntervalMs =
    globalConfig.demoMode === true
      ? (globalConfig.demoPageConsumeInterval ?? 0)
      : 0;

  const cancelCurrentProcess = useCallback(() => {
    activeProcessRunIdRef.current += 1;
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    destroy();
  }, [destroy]);

  useEffect(() => {
    return () => {
      cancelCurrentProcess();
    };
  }, [cancelCurrentProcess]);

  useEffect(() => {
    if (error instanceof Error) {
      messageApi.error(error.message);
    }
  }, [error, messageApi]);

  const visibleLogItems = useMemo(() => {
    return getVisibleLogItems(logItems);
  }, [logItems]);

  const resetPageState = useCallback(() => {
    secondRoundTaskIdRef.current += 1;
    cancelCurrentProcess();
    firstRoundAnswerRef.current = null;
    secondRoundAnswerRef.current = null;
    setDetectStarted(false);
    setDetecting(false);
    setSecondRoundPending(false);
    setLatestDetectTime(null);
    setLogItems([]);
    setFirstRoundAnswer(null);
    setSecondRoundAnswer(null);
    setRecommendationDecision(null);
    setAdoptSubmitting(false);
    setDetectionProject(null);
  }, [cancelCurrentProcess]);

  const prepareStageLogs = useCallback((stage: 1 | 2 | 3, names: string[]) => {
    setLogItems(prev => {
      const nextWithoutStage = prev.filter(item => item.stage !== stage);
      return [...nextWithoutStage, ...createStageLogItems(names, stage)];
    });
  }, []);

  const runProcessStage = useCallback(
    async ({
      payload,
      stage,
      processNames,
      stopOnAnswer,
      onAnswer,
    }: {
      payload: EacProcessPayload | EacProcessPayloadSecond;
      stage: 1 | 2;
      processNames: string[];
      stopOnAnswer?: boolean;
      onAnswer: (item: EacProcessStreamItem) => void;
    }) => {
      const runId = activeProcessRunIdRef.current + 1;
      activeProcessRunIdRef.current = runId;
      abortControllerRef.current?.abort();
      destroy();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      const body = await runEacProcessStream(payload, abortController);
      const reader = body.getReader();
      const decoder = new TextDecoder();
      const cache = { current: '' };
      let answerReceived = false;
      let answerHandled = false;
      let stageErrorMessage = '';

      lastQueueConsumeAtRef.current = 0;
      createQueue({
        onItem: item => {
          const now = Date.now();
          const elapsed =
            lastQueueConsumeAtRef.current > 0
              ? now - lastQueueConsumeAtRef.current
              : 0;
          lastQueueConsumeAtRef.current = now;

          if (item.type === 'error') {
            console.log('[EAC_LOG_DEBUG][consume]', {
              stage,
              elapsed,
              minIntervalMs: pageConsumeIntervalMs,
              itemType: item.type,
              category: item.category,
              name: item.name,
              processName: item.processName,
              content: item.content,
            });
            stageErrorMessage = item.content || '检测失败，请重试';
            return;
          }

          const statusInfo = getProcessStatusFromStreamItem(item);
          console.log('[EAC_LOG_DEBUG][consume]', {
            stage,
            elapsed,
            minIntervalMs: pageConsumeIntervalMs,
            itemType: item.type,
            category: item.category,
            name: item.name,
            processName: item.processName,
            content: item.content,
            mappedStatus: statusInfo,
          });
          if (statusInfo && processNames.includes(statusInfo.processName)) {
            setLogItems(prev =>
              upsertLogStatus(
                prev,
                stage,
                statusInfo.processName,
                statusInfo.status
              )
            );
          }

          if (item.type === 'answer' && !answerHandled) {
            answerReceived = true;
            answerHandled = true;
            onAnswer(item);
          }
        },
        minIntervalMs: pageConsumeIntervalMs,
      });

      try {
        let done = false;
        let shouldStopReading = false;

        while (!done) {
          const { value, done: streamDone } = await reader.read();
          done = streamDone;

          if (!value) continue;

          const chunk = decoder.decode(value, { stream: true });
          const items = parseEacProcessStreamChunk(chunk, cache);
          for (const item of items) {
            console.log('[EAC_LOG_DEBUG][enqueue]', {
              stage,
              minIntervalMs: pageConsumeIntervalMs,
              itemType: item.type,
              category: item.category,
              name: item.name,
              processName: item.processName,
              content: item.content,
            });
            enqueue(item);
            if (item.type === 'error') {
              done = true;
              break;
            }

            if (stopOnAnswer && item.type === 'answer') {
              shouldStopReading = true;
              done = true;
              break;
            }
          }

          if (shouldStopReading) {
            break;
          }
        }

        if (shouldStopReading) {
          await reader.cancel();
        }

        if (!shouldStopReading && cache.current.trim()) {
          const remainItems = parseEacProcessStreamChunk('', cache);
          remainItems.forEach(item => enqueue(item));
        }

        await flush();

        if (stageErrorMessage) {
          throw new Error(stageErrorMessage);
        }

        return answerReceived;
      } finally {
        if (activeProcessRunIdRef.current === runId) {
          abortControllerRef.current = null;
          destroy();
        }
      }
    },
    [createQueue, destroy, enqueue, flush, pageConsumeIntervalMs]
  );

  const handleStartDetect = useCallback(async () => {
    setDetecting(true);
    if (!selectedProjectCode) {
      setDetecting(false);
      return;
    }

    try {
      // 每次手动检测前，先拉取项目详情，保证中间区展示数据为最新项目详情。
      const latestProjectDetail =
        await getEacProjectDetail(selectedProjectCode);
      const detectTime = new Date();

      setDetectionProject(latestProjectDetail);
      setDetectStarted(true);
      setDetecting(true);
      setSecondRoundPending(false);
      setLatestDetectTime(detectTime);
      setFirstRoundAnswer(null);
      setSecondRoundAnswer(null);
      setRecommendationDecision(null);
      setAdoptSubmitting(false);
      firstRoundAnswerRef.current = null;
      secondRoundAnswerRef.current = null;
      prepareStageLogs(1, []);
      prepareStageLogs(2, []);
      prepareStageLogs(3, []);

      const firstRoundPayload = buildFirstRoundPayload(
        latestProjectDetail.code
      );
      const firstStageProcessNames = getFirstRoundProcessNames();
      prepareStageLogs(1, firstStageProcessNames);
      console.log('runProcessStage====', firstRoundPayload);

      const firstStageHasAnswer = await runProcessStage({
        payload: firstRoundPayload,
        stage: 1,
        processNames: firstStageProcessNames,
        stopOnAnswer: true,
        onAnswer: item => {
          const parsed = parseFirstRoundAnswer(item.content);
          if (!parsed) return;
          firstRoundAnswerRef.current = parsed;
          setFirstRoundAnswer(parsed);
        },
      });

      if (!firstStageHasAnswer || !firstRoundAnswerRef.current) {
        setDetecting(false);
        setDetectStarted(false);
        throw new Error('检测失败，请重试');
      }

      if (isRiskFree(firstRoundAnswerRef.current)) {
        setDetecting(false);
        prepareStageLogs(2, []);
        prepareStageLogs(3, []);
        return;
      }

      const secondRoundTaskId = secondRoundTaskIdRef.current + 1;
      secondRoundTaskIdRef.current = secondRoundTaskId;
      setSecondRoundPending(true);
      const secondRoundPayload = buildSecondRoundPayload(
        firstRoundAnswerRef.current
      );
      const secondStageProcessNames = getSecondRoundProcessNames();
      prepareStageLogs(2, secondStageProcessNames);
      setDetecting(false);

      try {
        await runProcessStage({
          payload: secondRoundPayload,
          stage: 2,
          processNames: secondStageProcessNames,
          onAnswer: item => {
            const parsed = parseSecondRoundAnswer(item.content);
            if (!parsed) return;
            secondRoundAnswerRef.current = parsed;
            setSecondRoundAnswer(parsed);
          },
        });
      } finally {
        if (secondRoundTaskIdRef.current === secondRoundTaskId) {
          setSecondRoundPending(false);
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      }
      messageApi.error((err as Error).message || '检测失败，请重试');
    } finally {
      setDetecting(false);
    }
  }, [
    messageApi,
    prepareStageLogs,
    runProcessStage,
    selectedProjectCode,
  ]);

  const handleRetryRecommendation = useCallback(async () => {
    if (!firstRoundAnswerRef.current) return;

    const secondRoundTaskId = secondRoundTaskIdRef.current + 1;
    secondRoundTaskIdRef.current = secondRoundTaskId;
    cancelCurrentProcess();
    setRecommendationDecision(null);
    setSecondRoundAnswer(null);
    secondRoundAnswerRef.current = null;
    setSecondRoundPending(true);
    const retryPayload = buildSecondRoundPayload(
      firstRoundAnswerRef.current,
      secondRoundAnswer?.已推荐方案列表 || []
    );
    const secondStageProcessNames = getSecondRoundProcessNames();
    // “重新推荐纠偏方案”作为第二轮过程节点前置步骤展示。
    prepareStageLogs(2, [RETRY_ACTION_LOG_NAME, ...secondStageProcessNames]);
    setLogItems(prev =>
      upsertLogStatus(prev, 2, RETRY_ACTION_LOG_NAME, 'completed')
    );
    prepareStageLogs(3, []);

    try {
      await runProcessStage({
        payload: retryPayload,
        stage: 2,
        processNames: secondStageProcessNames,
        onAnswer: item => {
          const parsed = parseSecondRoundAnswer(item.content);
          if (!parsed) return;
          secondRoundAnswerRef.current = parsed;
          setSecondRoundAnswer(parsed);
        },
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        return;
      }
      messageApi.error((err as Error).message || '重新推荐失败，请稍后重试');
    } finally {
      if (secondRoundTaskIdRef.current === secondRoundTaskId) {
        setSecondRoundPending(false);
      }
    }
  }, [
    RETRY_ACTION_LOG_NAME,
    cancelCurrentProcess,
    messageApi,
    prepareStageLogs,
    runProcessStage,
    secondRoundAnswer,
  ]);

  const handleRejectRecommendation = useCallback(() => {
    setRecommendationDecision('rejected');
    // 用户放弃推荐后，日志末尾追加已完成操作记录。
    setLogItems(prev =>
      upsertLogStatus(prev, 3, REJECT_ACTION_LOG_NAME, 'completed')
    );
  }, [REJECT_ACTION_LOG_NAME]);

  const handleAdoptRecommendation = useCallback(async () => {
    if (!detectionProject) return;

    const adoptPayload = extractAdoptPayload(secondRoundAnswerRef.current);
    if (!adoptPayload) {
      messageApi.warning('当前方案缺少可提交的更新数据');
      return;
    }

    setAdoptSubmitting(true);
    prepareStageLogs(3, ['执行方案回写数据']);
    setLogItems(prev =>
      upsertLogStatus(prev, 3, '执行方案回写数据', 'running')
    );

    try {
      await adoptEacRecommendation(detectionProject.code, adoptPayload);
      setLogItems(prev =>
        upsertLogStatus(prev, 3, '执行方案回写数据', 'completed')
      );
      // 用户采纳方案后，日志末尾追加已完成操作记录。
      setLogItems(prev =>
        upsertLogStatus(prev, 3, ADOPT_ACTION_LOG_NAME, 'completed')
      );
      setRecommendationDecision('adopted');
      messageApi.success('方案采纳成功');
    } catch (err) {
      setLogItems(prev => prev.filter(item => item.stage !== 3));
      messageApi.error((err as Error).message || '方案采纳失败，请稍后重试');
    } finally {
      setAdoptSubmitting(false);
    }
  }, [ADOPT_ACTION_LOG_NAME, detectionProject, messageApi, prepareStageLogs]);

  const handleProjectChange = useCallback(
    (projectCode: string | undefined) => {
      resetPageState();
      setSelectedProjectCode(projectCode);
    },
    [resetPageState]
  );

  return (
    <div className="min-h-screen bg-[#ecf1f5] text-[#1f2937]">
      <AppHeader />
      {contextHolder}
      <PageHeader />

      <main className="h-[calc(100vh-56px)] bg-white">
        <div className="flex h-full min-w-0">
          <div
            className="min-w-0"
            style={{ flex: '1 1 0%', maxWidth: '600px' }}
          >
            <ProjectSidebar
              projects={projectList}
              selectedProjectCode={selectedProjectCode}
              onProjectChange={handleProjectChange}
              loading={isLoading}
              disabled={detecting || secondRoundPending || adoptSubmitting}
              detecting={detecting || secondRoundPending}
              onStartDetect={handleStartDetect}
            />
          </div>

          <section
            className="flex min-w-0 items-stretch"
            style={{ flex: '3 1 0%' }}
          >
            <div className="flex min-w-0 flex-1 items-center justify-center px-0 py-0">
              {isLoading ? (
                <Spin size="large" />
              ) : detectionProject && detectStarted ? (
                <DetectionContent
                  project={detectionProject}
                  detectStarted={detectStarted}
                  detecting={detecting}
                  latestDetectTime={latestDetectTime}
                  firstRoundAnswer={firstRoundAnswer}
                  secondRoundAnswer={secondRoundAnswer}
                  secondRoundPending={secondRoundPending}
                  recommendationDecision={recommendationDecision}
                  adoptSubmitting={adoptSubmitting}
                  onRejectRecommendation={handleRejectRecommendation}
                  onRetryRecommendation={handleRetryRecommendation}
                  onAdoptRecommendation={handleAdoptRecommendation}
                />
              ) : (
                <ContentEmptyState />
              )}
            </div>
          </section>

          <div
            className="min-w-0"
            style={{ flex: '1 1 0%', maxWidth: '600px' }}
          >
            <LogPanel logItems={visibleLogItems} />
          </div>
        </div>
      </main>
    </div>
  );
};

export default EbpEacEnhancementPage;
